import React, { useState, useEffect } from "react";
import PlanService from "../../../../services/PlanService";
import SubscriptionService from "../../../../services/SubscriptionService";
import { toast } from "react-toastify";
import ConfirmModal from "../../../../modals/ConfirmModal";
import SubscriptionLogsModal from "../modals/SubscriptionLogsModal";
import { validityInMonths } from "../../../../utils/OptionUtils";

const Subscription = (props) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState(props.planId || null);
    const [validities, setValidities] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const validityOptions = ["Monthly", "3 Monthly", "6 Monthly", "Yearly"];
    const [showSubscriptionLogsModal, setShowSubscriptionLogsModal] = useState(false);
    const [subscriptionLogs, setSubscriptionLogs] = useState([]);
    const [subscriptionPlan, setSubscriptionPlan] = useState({});
    const [tempPlanId, setTempPlanId] = useState(null);

    const fetchPlansWithGroupsAndChecklists = async () => {
        setLoading(true);
        try {
            const response = await PlanService.getAll();
            if (response.status === 200) {
                const plans = response?.data?.data?.plans;
                setPlans(plans);
                const initialValidities = {};
                plans.forEach((plan) => {
                    initialValidities[plan.id] = plan.duration;
                });
                setValidities(initialValidities);
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            console.error("Error in fetching plans", error);
            toast.error(error?.message || "An error occurred");
        } finally {
            setShowConfirmModal(false);
            setTempPlanId(null);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (props.subscriptions?.length > 0) {
            const latestActiveSubscription = props.subscriptions
                .filter(sub => sub.status === "active")
                .reduce((latest, current) =>
                    current.id > latest.id ? current : latest,
                    props.subscriptions[0]
                );

            const activePlanId = latestActiveSubscription?.plan?.id;
            setSelectedPlanId(activePlanId);
            setSubscriptionPlan(latestActiveSubscription);
        }
        fetchPlansWithGroupsAndChecklists();
    }, [props.subscriptions]);

    useEffect(() => {
        if (props.planId) setSelectedPlanId(props.planId);
    }, [props.planId]);

    const handlePlanSelection = async (planId) => {
        try {
            setLoading(true);
            const validity = validityInMonths[validities[planId]?.toLowerCase()];
            const startDate = new Date();
            const endDate = new Date(startDate);
            endDate.setMonth(startDate.getMonth() + validity);
            const data = {
                plan_id: planId,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                customer_id: props.customerId,
            };

            const response = await SubscriptionService.create({ data });

            if (response.status === 201) {
                const newSubscription = response?.data?.data;
                setSubscriptionPlan(newSubscription);
                toast.success(response?.data?.message);
                setShowConfirmModal(false);
                props.setPlanId(planId);
                props.setCustomer((prevCustomer) => {
                    const updatedCustomer = {
                        ...prevCustomer,
                        subscriptions: [
                            ...(prevCustomer?.subscriptions || []).filter(sub => sub.id !== newSubscription.id),
                            { ...newSubscription, subscriptionTickets: [] } // Reset tickets to empty
                        ],
                    };
                    return updatedCustomer;
                });
            }
             else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            console.error("Error in creating subscription:", error);
            toast.error(error?.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const handleValidityChange = (planId, value) => {
        setValidities((prevValidities) => ({
            ...prevValidities,
            [planId]: value,
        }));
    };

    const handleCancel = () => {
        setTempPlanId(null);
        setShowConfirmModal(false);
    }
    const handleConfirm = async () => {
        if (tempPlanId) {
            await handlePlanSelection(tempPlanId);
            setSelectedPlanId(tempPlanId);
        }
        setShowConfirmModal(false);
        setTempPlanId(null);
    };
    const handleConfirmModal = (id) => {
        setTempPlanId(id);
        setShowConfirmModal(true);
    };

    const handleShowSubscriptionLogs = async() => {
        try {
            const response = await SubscriptionService.subscriptionByCustomer(props.customerId);
            if (response.status === 200) {
                setShowSubscriptionLogsModal(true);
                setSubscriptionLogs(response?.data?.data);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    }
    const handleClose = () => {
        setShowSubscriptionLogsModal(false);
    }
    return (
        <>
        <div className="tab-content">
            <div
                className="tab-pane fade show active"
                id="subscriptionListPane"
                role="tabpanel"
                aria-labelledby="subscriptionListTab"
            >
                <div className="card">
                    <div className="card-header">
                        <div className="card-title">
                            <div className="row align-items-end">
                                <div className="col">
                                    <h2 className="header-title">{props.company}</h2>
                                </div>
                                <div className="col-auto">
                                    <button className={`btn ${subscriptionPlan.plan?.name ? "btn-success" : "btn-primary"} btn-sm`}  onClick={() => handleShowSubscriptionLogs()}>Active Plan: {subscriptionPlan.plan?.name || "No active plan"} | (Valid till: {subscriptionPlan.end_date || "N/A"})</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="row">
                                {plans
                                    .sort((a, b) => (a.id === selectedPlanId ? -1 : b.id === selectedPlanId ? 1 : 0))
                                    .filter((plan) => plan.checklists.length > 0)
                                    .map((plan) => (
                                        <div key={plan.id} className="col-md-6 col-lg-4 mb-4">
                                            <div
                                                className={`card h-100 shadow ${selectedPlanId === plan.id
                                                        ? "border border-primary custom-shadow mb-0"
                                                        : ""
                                                    }`}
                                            >
                                                <div className="card-header d-flex justify-content-between align-items-center">
                                                    <h4>{plan.name}</h4>
                                                    {selectedPlanId === plan.id && (
                                                    <button className="btn btn-sm btn-primary" >
                                                        Ticket
                                                    </button>
                                                    )}
                                                </div>
                                                <div className="card-body overflow-auto" style={{ height: '300px', maxHeight: '300px' , scrollbarWidth: 'thin'}}>
                                                    {Object.entries(
                                                        plan.checklists.reduce((acc, checklist) => {
                                                            if(!checklist.group) return acc;
                                                            const groupId = checklist.group.id;
                                                            if(!acc[groupId]) {
                                                                acc[groupId] = {
                                                                    name: checklist.group.name,
                                                                    checklists: []
                                                                }
                                                            }
                                                            acc[groupId].checklists.push(checklist);
                                                            return acc;
                                                        }, {})
                                                    )
                                                    .map(([groupId, group]) => (
                                                        <div key={groupId} className="mb-3">
                                                            <h5 className="text-primary">{group.name}</h5>
                                                            <ul>
                                                            {group.checklists.map((checklist, index) => (
                                                                <li
                                                                    key={index}
                                                                    className="custom-checklist-item text-muted"
                                                                >
                                                                    {checklist.name}
                                                                </li>                                                            
                                                                ))
                                                            }
                                                            </ul>
                                                        </div>
                                                    ))
                                                    }
                                                </div>
                                                <div className="card-footer">
                                                    {!plan.is_billing_cycle && (
                                                        <select
                                                            className="form-select mb-2"
                                                            value={validities[plan.id] || "Monthly"}
                                                            onChange={(e) =>
                                                                handleValidityChange(plan.id, e.target.value)
                                                            }
                                                        >
                                                            {validityOptions.map((option, index) => (
                                                                <option key={index} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                    {plan.is_billing_cycle && (
                                                        <p className="text-dark">
                                                           <b> Duration: {plan?.duration}</b>
                                                        </p>
                                                    )}
                                                    <button
                                                        className={`btn btn-sm ${selectedPlanId === plan.id
                                                                ? "btn-primary"
                                                                : "btn-secondary"
                                                            } w-100`}
                                                        onClick={() => handleConfirmModal(plan.id)}
                                                        disabled={selectedPlanId === plan.id ? true : false}
                                                    >
                                                        {selectedPlanId === plan.id ? "Selected" : "Select Plan"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        {showConfirmModal && (
            <ConfirmModal
                show={showConfirmModal}
                onCancel={ handleCancel }
                onConfirm={ handleConfirm }
                id={tempPlanId}
                heading="Confirm Plan"
                content="Are you sure you want to select this plan?"
            />
        )}
        {showSubscriptionLogsModal && (
            <SubscriptionLogsModal
                show={showSubscriptionLogsModal}
                handleClose={handleClose}
                subscriptionLogs={subscriptionLogs}
            />
        )}
        </>
    );
};

export default Subscription;
